import { v4 as uuidv4 } from 'uuid';

export interface MockPresentation {
  id: string;
  title: string;
  presenter: string;
  description: string;
  date: string;
  status: 'draft' | 'active' | 'completed';
  questions: MockQuestion[];
  createdAt: string;
  updatedAt: string;
}

export interface MockQuestion {
  id: string;
  text: string;
  type: 'multiple_choice';
  options: string[];
  required: boolean;
  order: number;
}

export interface MockUser {
  id: string;
  username: string;
  email: string;
  attributes: {
    given_name: string;
    family_name: string;
    'custom:role': 'admin' | 'presenter' | 'voter';
  };
}

export interface MockVote {
  id: string;
  userId: string;
  presentationId: string;
  questionId: string;
  selectedOption: number;
  timestamp: string;
}

// Mock Users
export const mockUsers: MockUser[] = [
  {
    id: '1',
    username: 'admin',
    email: 'admin@bessemer.com',
    attributes: {
      given_name: 'Sarah',
      family_name: 'Chen',
      'custom:role': 'admin'
    }
  },
  {
    id: '2',
    username: 'presenter',
    email: 'presenter@bessemer.com',
    attributes: {
      given_name: 'Michael',
      family_name: 'Rodriguez',
      'custom:role': 'presenter'
    }
  },
  {
    id: '3',
    username: 'voter',
    email: 'voter@bessemer.com',
    attributes: {
      given_name: 'Emily',
      family_name: 'Watson',
      'custom:role': 'voter'
    }
  }
];

// Mock Presentations
export const mockPresentations: MockPresentation[] = [
  {
    id: '1',
    title: 'TechFlow AI - Series A Investment Opportunity',
    presenter: 'Sarah Chen',
    description: 'AI-powered workflow automation platform targeting enterprise customers. Strong product-market fit with 300% YoY growth and $2M ARR.',
    date: '2025-01-28',
    status: 'active',
    questions: [
      {
        id: 'q1',
        text: 'What is your overall investment recommendation for TechFlow AI?',
        type: 'multiple_choice',
        options: [
          'Strong Yes - Lead the round',
          'Yes - Participate significantly', 
          'Maybe - Small participation',
          'No - Pass on this opportunity'
        ],
        required: true,
        order: 0
      },
      {
        id: 'q2',
        text: 'What is the biggest risk factor for this investment?',
        type: 'multiple_choice',
        options: [
          'Market competition from incumbents',
          'Technical execution challenges',
          'Customer acquisition costs',
          'Team scaling concerns',
          'Economic downturn impact'
        ],
        required: true,
        order: 1
      },
      {
        id: 'q3',
        text: 'What valuation range seems appropriate for this Series A?',
        type: 'multiple_choice',
        options: [
          '$15-20M pre-money',
          '$20-25M pre-money',
          '$25-30M pre-money',
          '$30-35M pre-money',
          'Above $35M pre-money'
        ],
        required: true,
        order: 2
      },
      {
        id: 'q4',
        text: 'How confident are you in the management team?',
        type: 'multiple_choice',
        options: [
          'Very confident - Exceptional team',
          'Confident - Strong team',
          'Somewhat confident - Good team',
          'Neutral - Average team',
          'Concerned - Weak team'
        ],
        required: true,
        order: 3
      }
    ],
    createdAt: '2025-01-25T10:00:00Z',
    updatedAt: '2025-01-28T09:00:00Z'
  },
  {
    id: '2',
    title: 'GreenEnergy Solutions - Seed Round Evaluation',
    presenter: 'Michael Rodriguez',
    description: 'Renewable energy storage solutions for residential and commercial markets. Innovative battery technology with 40% cost reduction.',
    date: '2025-01-25',
    status: 'completed',
    questions: [
      {
        id: 'q5',
        text: 'Should we proceed with the seed investment in GreenEnergy Solutions?',
        type: 'multiple_choice',
        options: [
          'Yes - Strong opportunity',
          'Yes - With conditions',
          'Maybe - Need more diligence',
          'No - Not aligned with thesis'
        ],
        required: true,
        order: 0
      },
      {
        id: 'q6',
        text: 'What is the most compelling aspect of this opportunity?',
        type: 'multiple_choice',
        options: [
          'Proprietary technology advantage',
          'Large addressable market',
          'Experienced founding team',
          'Strong early traction',
          'Favorable regulatory environment'
        ],
        required: true,
        order: 1
      },
      {
        id: 'q7',
        text: 'What investment amount would you recommend?',
        type: 'multiple_choice',
        options: [
          '$500K - $1M',
          '$1M - $2M',
          '$2M - $3M',
          '$3M - $5M',
          'Above $5M'
        ],
        required: true,
        order: 2
      }
    ],
    createdAt: '2025-01-20T14:00:00Z',
    updatedAt: '2025-01-25T16:00:00Z'
  },
  {
    id: '3',
    title: 'HealthTech Innovations - Series B Follow-on',
    presenter: 'Emily Watson',
    description: 'Digital health platform expanding into telehealth services. Existing portfolio company seeking additional funding for market expansion.',
    date: '2025-01-30',
    status: 'draft',
    questions: [
      {
        id: 'q8',
        text: 'Should we participate in the Series B follow-on round?',
        type: 'multiple_choice',
        options: [
          'Yes - Maintain pro-rata',
          'Yes - Increase position',
          'Partial - Reduce position',
          'No - Exit opportunity'
        ],
        required: true,
        order: 0
      },
      {
        id: 'q9',
        text: 'How do you rate the company\'s progress since Series A?',
        type: 'multiple_choice',
        options: [
          'Exceeded expectations',
          'Met expectations',
          'Slightly below expectations',
          'Significantly below expectations'
        ],
        required: true,
        order: 1
      }
    ],
    createdAt: '2025-01-28T11:00:00Z',
    updatedAt: '2025-01-28T11:00:00Z'
  },
  {
    id: '4',
    title: 'FinTech Disruptor - Pre-Seed Opportunity',
    presenter: 'David Kim',
    description: 'Next-generation payment processing platform for SMBs. Former PayPal executives with strong industry connections.',
    date: '2025-02-02',
    status: 'draft',
    questions: [
      {
        id: 'q10',
        text: 'Is this pre-seed opportunity worth pursuing?',
        type: 'multiple_choice',
        options: [
          'Yes - High potential',
          'Yes - Worth exploring',
          'Maybe - Need more info',
          'No - Not interested'
        ],
        required: true,
        order: 0
      }
    ],
    createdAt: '2025-01-28T15:00:00Z',
    updatedAt: '2025-01-28T15:00:00Z'
  },
  {
    id: '5',
    title: 'Enterprise SaaS Platform - Growth Stage',
    presenter: 'Jennifer Liu',
    description: 'B2B software platform for supply chain management. Strong metrics with $10M ARR and 150% net revenue retention.',
    date: '2025-02-05',
    status: 'draft',
    questions: [
      {
        id: 'q11',
        text: 'What is your investment thesis for this growth-stage opportunity?',
        type: 'multiple_choice',
        options: [
          'Strong fundamentals - Invest',
          'Good metrics - Consider',
          'Uncertain - More diligence needed',
          'Overvalued - Pass'
        ],
        required: true,
        order: 0
      },
      {
        id: 'q12',
        text: 'How do you view the competitive landscape?',
        type: 'multiple_choice',
        options: [
          'Clear market leader position',
          'Strong competitive advantage',
          'Competitive but differentiated',
          'Highly competitive market',
          'Losing to competitors'
        ],
        required: true,
        order: 1
      }
    ],
    createdAt: '2025-01-29T09:00:00Z',
    updatedAt: '2025-01-29T09:00:00Z'
  }
];

// Mock Votes
export const mockVotes: MockVote[] = [
  // TechFlow AI votes
  { id: 'v1', userId: '1', presentationId: '1', questionId: 'q1', selectedOption: 1, timestamp: '2025-01-28T10:15:00Z' },
  { id: 'v2', userId: '2', presentationId: '1', questionId: 'q1', selectedOption: 0, timestamp: '2025-01-28T10:16:00Z' },
  { id: 'v3', userId: '3', presentationId: '1', questionId: 'q1', selectedOption: 1, timestamp: '2025-01-28T10:17:00Z' },
  
  { id: 'v4', userId: '1', presentationId: '1', questionId: 'q2', selectedOption: 0, timestamp: '2025-01-28T10:18:00Z' },
  { id: 'v5', userId: '2', presentationId: '1', questionId: 'q2', selectedOption: 2, timestamp: '2025-01-28T10:19:00Z' },
  { id: 'v6', userId: '3', presentationId: '1', questionId: 'q2', selectedOption: 1, timestamp: '2025-01-28T10:20:00Z' },
  
  // GreenEnergy Solutions votes
  { id: 'v7', userId: '1', presentationId: '2', questionId: 'q5', selectedOption: 0, timestamp: '2025-01-25T14:30:00Z' },
  { id: 'v8', userId: '2', presentationId: '2', questionId: 'q5', selectedOption: 1, timestamp: '2025-01-25T14:31:00Z' },
  { id: 'v9', userId: '3', presentationId: '2', questionId: 'q5', selectedOption: 0, timestamp: '2025-01-25T14:32:00Z' },
];

// Helper functions
export const getPresentationById = (id: string): MockPresentation | undefined => {
  return mockPresentations.find(p => p.id === id);
};

export const getVotesByPresentationId = (presentationId: string): MockVote[] => {
  return mockVotes.filter(v => v.presentationId === presentationId);
};

export const getVotesByQuestionId = (questionId: string): MockVote[] => {
  return mockVotes.filter(v => v.questionId === questionId);
};

export const getUserById = (id: string): MockUser | undefined => {
  return mockUsers.find(u => u.id === id);
};

export const authenticateUser = (username: string, password: string): MockUser | null => {
  // Simple mock authentication - in real app this would be secure
  const user = mockUsers.find(u => u.username === username);
  if (user && password === 'password') {
    return user;
  }
  return null;
};

// Local storage helpers
export const saveToLocalStorage = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const loadFromLocalStorage = (key: string, defaultValue: any = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

// Initialize local storage with mock data
export const initializeMockData = () => {
  // Only initialize if data doesn't exist
  if (!loadFromLocalStorage('presentations')) {
    saveToLocalStorage('presentations', mockPresentations);
  }
  if (!loadFromLocalStorage('users')) {
    saveToLocalStorage('users', mockUsers);
  }
  if (!loadFromLocalStorage('votes')) {
    saveToLocalStorage('votes', mockVotes);
  }
};
